import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Plus } from "lucide-react";
import { useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ClientDialog from "@/components/sites/ClientDialog";

export const INTERNAL = "__internal";
const LIMIT = 50;

// Single searchable client box: type to search ALL active clients on the server (name, legal name, email, VAT, phone), keyboard friendly,
// «create new» prefilled with what was typed; optional INTERNAL choice (ADM draft, no site yet).
export default function ClientPicker({ value, onChange, onPick, allowInternal = false, testid = "client-picker", initial = null }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(null);
  const [picked, setPicked] = useState(initial);
  const [creating, setCreating] = useState(false);
  const seq = useRef(0);
  useEffect(() => { api.get("/clients").then(({ data }) => setTotal(data.length)).catch(() => {}); }, [api]);
  useEffect(() => {
    if (!value || value === INTERNAL || picked?.id === value) return;
    api.get(`/clients/${value}`).then(({ data }) => { setPicked(data); onPick?.(data); }).catch(() => {});
  }, [api, value, picked]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const n = ++seq.current;
    setList(null);
    const t = setTimeout(() => api.get("/clients", { params: { q, limit: LIMIT + 1 } }).then(({ data }) => { if (n === seq.current) setList(data); }).catch(() => n === seq.current && setList([])), 200);
    return () => clearTimeout(t);
  }, [api, q, open]);
  const choose = (c) => { setPicked(c); onChange(c.id); onPick?.(c); setOpen(false); };
  const label = value === INTERNAL ? "Spazio interno MUSORA (bozza, nessun sito)" : picked?.name;
  return (
    <div className="flex gap-2" data-testid={testid}>
      <Popover modal open={open} onOpenChange={(o) => { setOpen(o); if (o) setQ(""); }}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} aria-label="Scegli il cliente" className="h-10 min-w-0 flex-1 justify-between bg-white font-normal" data-testid={`${testid}-select`}>
            <span className={`truncate ${label ? "text-slate-900" : "text-slate-500"}`} data-testid={`${testid}-value`}>{label || `Cerca e scegli il cliente${total != null ? ` (${total} attivi)` : ""}`}</span>
            <ChevronsUpDown size={15} className="ml-2 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[300px] p-0">
          <Command shouldFilter={false}>
            <CommandInput autoFocus value={q} onValueChange={setQ} placeholder="Scrivi nome, ragione sociale, email o P.IVA…" data-testid={`${testid}-search`} />
            <CommandList className="max-h-72">
              {list === null ? <p className="flex items-center gap-2 p-3 text-xs text-slate-500"><Loader2 size={13} className="animate-spin" />Ricerca…</p> : <>
                <CommandEmpty>Nessun cliente trovato{q.trim() ? ` per «${q.trim()}»` : ""}.</CommandEmpty>
                {allowInternal && !q.trim() && <CommandGroup><CommandItem value={INTERNAL} onSelect={() => { onChange(INTERNAL); onPick?.(null); setOpen(false); }} data-testid={`${testid}-internal`}>
                  <Check size={14} className={`mr-2 ${value === INTERNAL ? "opacity-100" : "opacity-0"}`} />Spazio interno MUSORA (bozza, nessun sito)</CommandItem></CommandGroup>}
                {list.length > 0 && <CommandGroup heading={q.trim() ? "Risultati" : "Clienti attivi (A-Z)"}>
                  {list.slice(0, LIMIT).map((c) => (
                    <CommandItem key={c.id} value={c.id} onSelect={() => choose(c)} data-testid={`${testid}-option-${c.id}`}>
                      <Check size={14} className={`mr-2 shrink-0 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                      <span className="min-w-0"><span className="block truncate">{c.name}</span>
                        {(c.legal_name || c.email || c.vat_number) && <span className="block truncate text-[11px] text-slate-500">{[c.legal_name, c.email, c.vat_number].filter(Boolean).join(" · ")}</span>}</span>
                    </CommandItem>))}
                  {list.length > LIMIT && <p className="px-3 py-2 text-[11px] text-slate-500">Altri risultati: continua a scrivere per restringere.</p>}
                </CommandGroup>}
              </>}
            </CommandList>
            <button type="button" onClick={() => { setOpen(false); setCreating(true); }} className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm text-sky-700 hover:bg-sky-50" data-testid={`${testid}-create-typed`}>
              <Plus size={14} />{q.trim() ? `Crea il cliente «${q.trim()}»` : "Nuovo cliente"}</button>
          </Command>
        </PopoverContent>
      </Popover>
      <Button type="button" variant="outline" className="h-10" onClick={() => setCreating(true)} data-testid={`${testid}-new`}><Plus size={15} className="mr-1" />Nuovo</Button>
      <ClientDialog open={creating} onOpenChange={setCreating} client={null} initialName={q.trim()} onSaved={(c) => { setTotal((t) => (t ?? 0) + 1); choose(c); }} />
    </div>
  );
}
